<script setup lang="ts">
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMediaQuery } from '@vueuse/core';
import { media } from '@ibgroup-tws-webapps/ratio.ui';

const STORAGE_KEY = 'ft2:openAcctBannerDismissed:v1';

const { t } = useI18n();

// Match the legacy `$media.l` breakpoint (>=1024px). Below this, the banner
// shows a side-by-side layout with an "Open Account" pill on the right.
// At/above, it switches to a centered layout with a gift icon next to the
// title and no button (the whole banner is clickable).
const isLargeViewport = useMediaQuery('(min-width: 1024px)');

const dismissed = ref(typeof sessionStorage !== 'undefined' && sessionStorage.getItem(STORAGE_KEY) === 'true');

const isDark = computed(() => media.scheme === 'dark');

// The banner is only dismissible on desktop; on mobile it always shows so the
// dismissed flag is ignored there.
const showBanner = computed(() => !isLargeViewport.value || !dismissed.value);

// `.com` gets the $10 promo copy; every other TLD gets a generic open-account banner
const promoText = computed(() => t('open_account_promo_banner'));

// Distinct CTA ids on Markets vs Elections Hub so GA4 can attribute banner
// clicks to the right page even when the reporting view collapses across URLs.
const ctaBtnId = computed(() =>
  typeof window !== 'undefined' && window.location.pathname.startsWith('/election-hub')
    ? 'a_open_accou_2'
    : 'a_open_accou_1',
);

const dismiss = () => {
  dismissed.value = true;
  if (typeof sessionStorage !== 'undefined') sessionStorage.setItem(STORAGE_KEY, 'true');
};

// Mirrors the legacy banner: strip the leftmost subdomain off the host and
// send the user to the marketing-site application form on the same registrable
// domain. Falls back gracefully when there's no subdomain.
const handleSignup = () => {
  dismiss();
  if (typeof window === 'undefined') return;
  const host = window.location.host;
  const domain = host.includes('.') ? host.replace(/^[^.]+\./, '') : host;
  window.location.href = `https://www.${domain}/Universal/Application?eventContract=T`;
};
</script>

<template>
  <div v-if="showBanner" class="open-acct-banner-wrap">
    <div
      class="open-acct-banner"
      :class="{ 'open-acct-banner--dark': isDark, 'open-acct-banner--desktop': isLargeViewport }"
      role="button"
      tabindex="0"
      :aria-label="promoText"
      data-type="cta"
      :data-cta-btn="ctaBtnId"
      @click="handleSignup"
      @keydown.enter="handleSignup"
      @keydown.space.prevent="handleSignup"
    >
      <div class="open-acct-banner__content" :class="{ 'open-acct-banner__content--desktop': isLargeViewport }">
        <svg
          v-if="isLargeViewport"
          class="open-acct-banner__gift-icon"
          viewBox="0 0 13 13"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <path
            d="M5.06 0C4.22764 0 3.542 0.685645 3.542 1.518C3.542 1.69603 3.57941 1.86467 3.63687 2.024H1.012C0.45287 2.024 0 2.47687 0 3.036V4.048H12.144V3.036C12.144 2.47687 11.6911 2.024 11.132 2.024H8.50712C8.56459 1.86467 8.602 1.69603 8.602 1.518C8.602 0.685645 7.91635 0 7.084 0C6.6951 0 6.34188 0.153887 6.072 0.398277C5.80212 0.153887 5.4489 0 5.06 0ZM5.06 1.012C5.3459 1.012 5.566 1.23209 5.566 1.518V2.024H5.06C4.77409 2.024 4.554 1.80391 4.554 1.518C4.554 1.23209 4.77409 1.012 5.06 1.012ZM7.084 1.012C7.3699 1.012 7.59 1.23209 7.59 1.518C7.59 1.80391 7.3699 2.024 7.084 2.024H6.578V1.518C6.578 1.23209 6.79809 1.012 7.084 1.012ZM0.506 5.06V11.638C0.506 12.1971 0.95887 12.65 1.518 12.65H5.566V5.06H0.506ZM6.578 5.06V12.65H10.626C11.1851 12.65 11.638 12.1971 11.638 11.638V5.06H6.578Z"
            fill="currentColor"
          />
        </svg>
        <p class="open-acct-banner__title" :class="isLargeViewport ? 'fs6' : 'fs7'">
          {{ promoText }}
        </p>
      </div>
      <button
        v-if="!isLargeViewport"
        class="open-acct-banner__cta"
        type="button"
        data-type="cta"
        :data-cta-btn="ctaBtnId"
        @click.stop="handleSignup"
      >
        {{ t('open_account') }}
      </button>
      <button
        v-if="isLargeViewport"
        class="open-acct-banner__close"
        type="button"
        :aria-label="t('open_account_promo_banner_dismiss')"
        @click.stop="dismiss"
      >
        <i class="ibi-close text-[12px]" />
      </button>
    </div>
  </div>
</template>

<style scoped>
.open-acct-banner-wrap {
  width: 100%;
}

.open-acct-banner {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 16px;
  border-radius: 16px;
  cursor: pointer;
  border: 3px solid transparent;
  background:
    linear-gradient(var(--p-content-background), var(--p-content-background)) padding-box,
    linear-gradient(135deg, var(--color-republican-light), var(--color-democrat-light)) border-box;
  box-shadow:
    0 0 14px -3px color-mix(in srgb, var(--color-republican-light) 40%, transparent),
    0 0 14px -3px color-mix(in srgb, var(--color-democrat-light) 40%, transparent);
  transition:
    box-shadow 0.2s ease,
    transform 0.2s ease;
}

.open-acct-banner:hover {
  box-shadow:
    0 0 20px -2px color-mix(in srgb, var(--color-republican-light) 50%, transparent),
    0 0 20px -2px color-mix(in srgb, var(--color-democrat-light) 50%, transparent);
  transform: translateY(-1px);
}

.open-acct-banner:focus-visible {
  outline: 2px solid var(--p-primary-color);
  outline-offset: 2px;
}

.open-acct-banner--desktop {
  justify-content: center;
}

.open-acct-banner--dark {
  background:
    linear-gradient(var(--p-content-background), var(--p-content-background)) padding-box,
    linear-gradient(135deg, var(--color-republican), var(--color-democrat)) border-box;
  box-shadow:
    0 0 14px -3px color-mix(in srgb, var(--color-republican) 40%, transparent),
    0 0 14px -3px color-mix(in srgb, var(--color-democrat) 40%, transparent);
}

.open-acct-banner--dark:hover {
  box-shadow:
    0 0 20px -2px color-mix(in srgb, var(--color-republican) 50%, transparent),
    0 0 20px -2px color-mix(in srgb, var(--color-democrat) 50%, transparent);
}

.open-acct-banner__content {
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
  min-width: 0;
}

.open-acct-banner__content--desktop {
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 8px;
  flex: 1 1 auto;
}

.open-acct-banner__gift-icon {
  flex-shrink: 0;
  color: var(--p-text-color);
  width: 12.144px;
  height: 12.65px;
  aspect-ratio: 24 / 25;
}

.open-acct-banner__title {
  font-weight: 900;
  letter-spacing: 0.025em;
  color: var(--p-text-color);
  margin: 0;
}

.open-acct-banner__title.fs6 {
  font-size: 16px;
  line-height: 20px;
}

.open-acct-banner__title.fs7 {
  font-size: 14px;
  line-height: 18px;
}

/* Pill CTA — gradient blue, 9999 radius, generous horizontal padding to read
   as a pill across all narrow widths. */
.open-acct-banner__cta {
  flex-shrink: 0;
  border: none;
  border-radius: 9999px;
  padding: 10px 24px;
  font-size: 14px;
  font-weight: 700;
  color: #fff;
  cursor: pointer;
  background: linear-gradient(160deg, #0f5fd9 0%, #2b7de9 40%, #5ba3f5 100%);
  box-shadow: 0 3px 10px -1px rgb(15 95 217 / 50%);
  transition:
    transform 0.15s ease,
    box-shadow 0.15s ease;
}

.open-acct-banner__cta:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 14px -1px rgb(15 95 217 / 60%);
}

.open-acct-banner--dark .open-acct-banner__cta {
  background: linear-gradient(160deg, #2b7de9 0%, #4d9fff 40%, #7ab8ff 100%);
  box-shadow: 0 3px 10px -1px rgb(43 125 233 / 60%);
}

.open-acct-banner__close {
  position: absolute;
  top: -10px;
  left: -10px;
  width: 22px;
  height: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff;
  color: #333;
  box-shadow: 0 1px 4px rgb(0 0 0 / 15%);
  border: none;
  border-radius: 50%;
  cursor: pointer;
  line-height: 1;
  opacity: 1;
  transition: opacity 0.2s ease;
}

.open-acct-banner--dark .open-acct-banner__close {
  background: #222;
  color: #ccc;
  box-shadow: 0 1px 4px rgb(0 0 0 / 40%);
}

@media (width >= 1024px) {
  .open-acct-banner__close {
    width: 24px;
    height: 24px;
    top: -12px;
    left: -12px;
  }
}

@media (hover) {
  .open-acct-banner__close {
    opacity: 0;
  }

  .open-acct-banner__close:hover,
  .open-acct-banner:hover .open-acct-banner__close {
    opacity: 1;
  }
}
</style>
